import type { IBenchmarkPoolApprovalContext, IResolvedBenchmarkPool } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import type { ILeaderboardTable } from '@/interfaces/powerMarkets/table/ILeaderboardTable.ts';

export class BenchmarkPoolApprovalRequiredError extends Error {
    readonly requiresUndersizedApproval = true;

    constructor (readonly preview: IResolvedBenchmarkPool, readonly approvalContext: IBenchmarkPoolApprovalContext) {
        super('Approve the undersized benchmark pool to continue.');
        this.name = 'BenchmarkPoolApprovalRequiredError';
    }
}

function responseMessage (data: unknown): string {
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) return data.map(responseMessage).filter(Boolean).join(' ');
    if (data && typeof data === 'object') {
        const body = data as Record<string, unknown>;
        return responseMessage(body.errors ?? body.message ?? body.title);
    }
    return '';
}

export function getBenchmarkPoolErrorMessage (error: unknown): string {
    if (error && typeof error === 'object') {
        // @lcp/http-auth rejects with the response itself, including status and data.
        const response = error as { status?: number; data?: unknown };
        if (response.status === 401 || response.status === 403) {
            return 'You do not have permission to use this pool. Check your sign-in and GB custom-index access.';
        }
        if (response.status === 404) {
            return 'The pool definition or revision could not be found. Retry to refresh it, or select another saved pool.';
        }
        const message = responseMessage(response.data);
        if (message && response.status === 400) return message;
        if (error instanceof Error && !(error instanceof TypeError)) return error.message;
    }
    return 'The benchmark pool request failed. Check your connection and retry.';
}

export function assertBenchmarkPoolTableSuccess (isPooled: boolean, result: ILeaderboardTable): void {
    // This endpoint returns validation failures as HTTP 200 with an empty table and null charts.
    // Require a known validation message too, so ordinary empty tables/titles remain valid.
    const validationMessage = /benchmark pool|custom.index|valid selected date range|skip-rate dataset|^Please select a fuel type\./i.test(result.tabHeader);
    if (isPooled && !result.table.data.length && result.chartOwner == null
        && result.chartOptimiser == null && validationMessage) {
        throw new Error(result.tabHeader);
    }
}
